import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CameraIcon, VideoIcon, EyeIcon, ShieldIcon, ArrowRightIcon, CheckCircle2Icon, StoreIcon, Building2Icon, WarehouseIcon, HospitalIcon, CpuIcon, MoonIcon, SmartphoneIcon, SearchIcon, ClipboardCheckIcon, PenToolIcon, WrenchIcon, HeadphonesIcon, TrendingDownIcon, ZapIcon, UsersIcon, HardDriveIcon, CloudIcon, ServerIcon, HelpCircleIcon, ChevronDownIcon } from 'lucide-react';
const services = [{
  icon: CameraIcon,
  title: 'Security Camera Installation',
  description: 'Professional installation of high-definition security cameras tailored to your facility layout and security needs.',
  color: 'text-blue-500',
  bg: 'bg-blue-50'
}, {
  icon: VideoIcon,
  title: 'CCTV Systems',
  description: 'Comprehensive Closed-Circuit Television systems providing reliable, continuous recording and local storage solutions.',
  color: 'text-indigo-500',
  bg: 'bg-indigo-50'
}, {
  icon: EyeIcon,
  title: '24/7 Remote Monitoring',
  description: 'Keep an eye on your business from anywhere in the world with secure, cloud-based remote monitoring access.',
  color: 'text-emerald-500',
  bg: 'bg-emerald-50'
}, {
  icon: ShieldIcon,
  title: 'Surveillance System Design',
  description: 'Custom-designed surveillance architectures that eliminate blind spots and integrate seamlessly with your network.',
  color: 'text-purple-500',
  bg: 'bg-purple-50'
}];
const industries = [{
  icon: StoreIcon,
  name: 'Retail',
  desc: 'Prevent loss and monitor customer traffic patterns.'
}, {
  icon: Building2Icon,
  name: 'Corporate',
  desc: 'Secure office spaces and manage employee safety.'
}, {
  icon: WarehouseIcon,
  name: 'Warehousing',
  desc: 'Monitor inventory and logistics operations 24/7.'
}, {
  icon: HospitalIcon,
  name: 'Healthcare',
  desc: 'Ensure patient safety and secure restricted areas.'
}];
const advancedFeatures = [{
  icon: CpuIcon,
  title: 'AI Analytics',
  desc: 'Facial recognition, object detection, and behavior analysis.'
}, {
  icon: MoonIcon,
  title: 'Night Vision',
  desc: 'Crystal clear imaging even in total darkness with IR technology.'
}, {
  icon: SmartphoneIcon,
  title: 'Mobile Integration',
  desc: 'View live feeds and receive alerts directly on your smartphone.'
}, {
  icon: SearchIcon,
  title: 'Smart Search',
  desc: 'Quickly find specific incidents with intelligent playback tools.'
}];
const processSteps = [{
  icon: ClipboardCheckIcon,
  title: 'Audit',
  desc: 'We assess your current security and identify vulnerabilities.'
}, {
  icon: PenToolIcon,
  title: 'Design',
  desc: 'Custom surveillance blueprint tailored to your facility.'
}, {
  icon: WrenchIcon,
  title: 'Install',
  desc: 'Professional setup by certified security technicians.'
}, {
  icon: HeadphonesIcon,
  title: 'Support',
  desc: 'Ongoing maintenance and 24/7 technical assistance.'
}];
export function VideoMonitoringPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  return <div className="bg-white" data-id="element-1950">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden bg-slate-900" data-id="element-1951">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-1952"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-1953"></div>
        <div className="absolute top-0 right-1/4 -translate-y-1/4 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-1954"></div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1955">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1956">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-1957">
              <CameraIcon className="w-4 h-4 mr-2 text-blue-400" data-id="element-1958" />
              Video & Monitoring Solutions
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4 tracking-tight" data-id="element-1959">
              Advanced
              <br className="hidden md:block" data-id="element-1960" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent to-accent-dark" data-id="element-1961">
                Video Monitoring
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-10" data-id="element-1962">
              From HD camera installations to 24/7 remote monitoring, we provide
              end-to-end video security solutions to keep your assets safe.
            </p>
            <button onClick={() => onNavigate('home')} className="bg-accent hover:bg-accent-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-1963">
              Secure Your Facility
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1964" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-slate-50" data-id="element-1965">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1966">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1967">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-1968">
              Comprehensive Video Solutions
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1969">
              We design, install, and maintain state-of-the-art video monitoring
              systems tailored to your specific operational needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1970">
            {services.map((service, i) => {
            const Icon = service.icon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-white rounded-2xl p-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1971">
                  <div className={`w-14 h-14 ${service.bg} rounded-2xl flex items-center justify-center mb-6`} data-id="element-1972">
                    <Icon className={`w-7 h-7 ${service.color}`} data-id="element-1973" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-3" data-id="element-1974">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed" data-id="element-1975">
                    {service.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* ROI of Video Security */}
      <section className="py-24 bg-slate-900 text-white relative overflow-hidden" data-id="element-1976">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-1977"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1978">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1979">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-1980">
              The ROI of Professional Video Security
            </h2>
            <p className="text-lg text-slate-400" data-id="element-1981">
              A modern surveillance system pays for itself by reducing loss and
              increasing operational efficiency.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-1982">
            {[{
            icon: TrendingDownIcon,
            stat: '67%',
            desc: 'Average reduction in internal and external theft.'
          }, {
            icon: ShieldIcon,
            stat: '45%',
            desc: 'Potential reduction in business insurance premiums.'
          }, {
            icon: ZapIcon,
            stat: '80%',
            desc: 'Faster incident resolution with smart search tools.'
          }, {
            icon: UsersIcon,
            stat: '24/7',
            desc: 'Continuous facility coverage without adding security staff.'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center" data-id="element-1983">
                <item.icon className="w-10 h-10 text-blue-400 mx-auto mb-4" data-id="element-1984" />
                <div className="text-4xl font-black text-white mb-2" data-id="element-1985">
                  {item.stat}
                </div>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-1986">
                  {item.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Advanced Features */}
      <section className="py-24 bg-white" data-id="element-1987">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1988">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1989">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-1990">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-1991">
                Next-Generation Security Features
              </h2>
              <p className="text-lg text-slate-600 mb-8" data-id="element-1992">
                Our systems go beyond simple recording. We leverage the latest
                in AI and cloud technology to provide proactive security that
                works for you.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1993">
                {advancedFeatures.map((feature, i) => <div key={i} className="flex gap-4" data-id="element-1994">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center" data-id="element-1995">
                      <feature.icon className="w-5 h-5 text-primary" data-id="element-1996" />
                    </div>
                    <div data-id="element-1997">
                      <h4 className="font-bold text-slate-900" data-id="element-1998">
                        {feature.title}
                      </h4>
                      <p className="text-sm text-slate-500" data-id="element-1999">{feature.desc}</p>
                    </div>
                  </div>)}
              </div>
            </motion.div>
            <div className="relative" data-id="element-2000">
              <img src="https://images.unsplash.com/photo-1557597774-9d273605dfa9?auto=format&fit=crop&q=80&w=800" alt="Security Monitoring" className="rounded-3xl shadow-2xl" data-id="element-2001" />
              <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-2xl shadow-xl border border-slate-100 hidden md:block" data-id="element-2002">
                <div className="flex items-center gap-4" data-id="element-2003">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center" data-id="element-2004">
                    <CheckCircle2Icon className="w-6 h-6 text-green-600" data-id="element-2005" />
                  </div>
                  <div data-id="element-2006">
                    <p className="text-sm font-bold text-slate-900" data-id="element-2007">
                      AI Enabled
                    </p>
                    <p className="text-xs text-slate-500" data-id="element-2008">
                      Real-time threat detection
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Storage & Recording Options */}
      <section className="py-24 bg-slate-50" data-id="element-2009">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2010">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2011">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2012">
              Flexible Storage Solutions
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2013">
              Choose how and where your video footage is stored based on your
              retention requirements and budget.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2014">
            {[{
            icon: HardDriveIcon,
            title: 'Local NVR',
            desc: 'Store footage on-site using a Network Video Recorder. Best for high-camera counts and avoiding monthly cloud storage fees.',
            pros: ['No monthly fees', 'High capacity', 'Works without internet']
          }, {
            icon: CloudIcon,
            title: 'Cloud Storage',
            desc: 'Footage is securely uploaded to the cloud. Best for multi-site businesses and protecting footage from physical theft or damage.',
            pros: ['Access anywhere', 'Disaster proof', 'Easy scalability']
          }, {
            icon: ServerIcon,
            title: 'Hybrid Solution',
            desc: 'The best of both worlds. Store high-res footage locally while backing up critical events and low-res streams to the cloud.',
            pros: ['Maximum reliability', 'Bandwidth efficient', 'Redundant backup']
          }].map((option, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm" data-id="element-2015">
                <div className="w-14 h-14 bg-blue-50 rounded-xl flex items-center justify-center mb-6" data-id="element-2016">
                  <option.icon className="w-7 h-7 text-blue-600" data-id="element-2017" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-2018">
                  {option.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6" data-id="element-2019">
                  {option.desc}
                </p>
                <ul className="space-y-2" data-id="element-2020">
                  {option.pros.map((pro, j) => <li key={j} className="flex items-center text-sm text-slate-700 font-medium" data-id="element-2021">
                      <CheckCircle2Icon className="w-4 h-4 text-green-500 mr-2" data-id="element-2022" />
                      {pro}
                    </li>)}
                </ul>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Industry Applications */}
      <section className="py-24 bg-slate-900 text-white" data-id="element-2023">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2024">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2025">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-2026">
              Industry Specific Solutions
            </h2>
            <p className="text-slate-400" data-id="element-2027">
              Every industry has unique security challenges. We provide tailored
              surveillance strategies for your specific sector.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-2028">
            {industries.map((industry, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white/5 border border-white/10 p-8 rounded-2xl hover:bg-white/10 transition-colors" data-id="element-2029">
                <industry.icon className="w-10 h-10 text-primary mb-6" data-id="element-2030" />
                <h3 className="text-xl font-bold mb-3" data-id="element-2031">{industry.name}</h3>
                <p className="text-slate-400 text-sm leading-relaxed" data-id="element-2032">
                  {industry.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-24 bg-white" data-id="element-2033">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2034">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2035">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2036">
              Our Implementation Process
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2037">
              From initial audit to ongoing support, we ensure your security
              transition is seamless.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8" data-id="element-2038">
            {processSteps.map((step, i) => <div key={i} className="relative text-center" data-id="element-2039">
                {i < processSteps.length - 1 && <div className="hidden md:block absolute top-10 left-1/2 w-full h-0.5 bg-slate-100 -z-10" data-id="element-2040"></div>}
                <div className="w-20 h-20 bg-white border-4 border-slate-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-2041">
                  <step.icon className="w-8 h-8 text-primary" data-id="element-2042" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-2043">
                  {step.title}
                </h3>
                <p className="text-slate-500 text-sm" data-id="element-2044">{step.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Video Security FAQ */}
      <section className="py-24 bg-slate-50" data-id="element-2045">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2046">
          <div className="text-center mb-16" data-id="element-2047">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4 flex items-center justify-center gap-3" data-id="element-2048">
              <HelpCircleIcon className="w-8 h-8 text-primary" data-id="element-2049" />
              Video Security FAQ
            </h2>
            <p className="text-lg text-slate-600" data-id="element-2050">
              Common questions about our surveillance systems.
            </p>
          </div>
          <div className="space-y-4" data-id="element-2051">
            {[{
            q: 'How long is footage stored?',
            a: 'Retention times are fully customizable. Depending on the storage capacity you choose (NVR hard drive size or cloud plan), you can store footage anywhere from 7 days to 1+ years.'
          }, {
            q: 'Can I view cameras remotely?',
            a: 'Yes. All our modern systems come with secure mobile apps and web portals, allowing authorized users to view live feeds and recorded footage from anywhere in the world.'
          }, {
            q: 'Do the cameras work at night?',
            a: 'Absolutely. We install cameras equipped with advanced Infrared (IR) technology and low-light sensors to provide clear, crisp imaging even in total darkness.'
          }, {
            q: 'What internet speed do I need?',
            a: 'It depends on the number of cameras and whether you use cloud storage. Local NVRs use very little internet bandwidth, while cloud cameras require higher upload speeds. We will assess your network during the site audit.'
          }, {
            q: 'Can I add more cameras later?',
            a: 'Yes, our systems are highly scalable. We can design the initial infrastructure with future expansion in mind, making it easy to add cameras as your business grows.'
          }].map((faq, i) => <div key={i} className="border border-slate-200 rounded-2xl overflow-hidden bg-white" data-id="element-2052">
                <button onClick={() => setActiveFaq(activeFaq === i ? null : i)} className="w-full flex items-center justify-between p-6 text-left hover:bg-slate-50 transition-colors" data-id="element-2053">
                  <span className="font-bold text-slate-900" data-id="element-2054">{faq.q}</span>
                  <motion.div animate={{
                rotate: activeFaq === i ? 180 : 0
              }} data-id="element-2055">
                    <ChevronDownIcon className="w-5 h-5 text-slate-400" data-id="element-2056" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-2057">
                  {activeFaq === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} className="overflow-hidden" data-id="element-2058">
                      <div className="p-6 pt-0 text-slate-600 border-t border-slate-100" data-id="element-2059">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary py-20 relative overflow-hidden" data-id="element-2060">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-2061"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 text-center" data-id="element-2062">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-2063">
            Ready to upgrade your security?
          </h2>
          <p className="text-xl text-blue-100 mb-10" data-id="element-2064">
            Contact us today for a free site walk-through and custom
            surveillance system design.
          </p>
          <button onClick={() => onNavigate('contact')} className="bg-white text-primary px-10 py-4 rounded-full font-bold text-lg hover:bg-slate-50 transition-all shadow-xl inline-flex items-center" data-id="element-2065">
            Schedule Consultation
            <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2066" />
          </button>
        </div>
      </section>
    </div>;
}